// SPDX-License-Identifier: AGPL-3.0-only

export interface SimulationDomainWindow {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly activeCellCount: number;
  readonly totalCellCount: number;
}

export interface AdaptiveSimulationDomainOptions {
  readonly enabled: boolean;
  readonly tileSizeCells: number;
  readonly initialPaddingCells: number;
  readonly edgeMarginCells: number;
  readonly wetDepthThresholdMeters: number;
}

export interface WetDomainEdges {
  readonly west: boolean;
  readonly north: boolean;
  readonly east: boolean;
  readonly south: boolean;
}

interface CellBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export class AdaptiveSimulationDomain {
  private readonly width: number;
  private readonly height: number;
  private readonly options: AdaptiveSimulationDomainOptions;
  private x = 0;
  private y = 0;
  private activeWidth: number;
  private activeHeight: number;

  constructor(width: number, height: number, options: AdaptiveSimulationDomainOptions) {
    this.width = width;
    this.height = height;
    this.options = {
      ...options,
      tileSizeCells: Math.max(4, Math.round(options.tileSizeCells)),
      initialPaddingCells: Math.max(1, Math.round(options.initialPaddingCells)),
      edgeMarginCells: Math.max(1, Math.round(options.edgeMarginCells)),
      wetDepthThresholdMeters: Math.max(0, options.wetDepthThresholdMeters)
    };
    this.activeWidth = width;
    this.activeHeight = height;
  }

  get window(): SimulationDomainWindow {
    return {
      x: this.x,
      y: this.y,
      width: this.activeWidth,
      height: this.activeHeight,
      activeCellCount: this.activeWidth * this.activeHeight,
      totalCellCount: this.width * this.height
    };
  }

  resetToMask(mask: ArrayLike<number>): void {
    const bounds = this.boundsForValues(mask, 0);
    if (!this.canUseAdaptiveWindow() || !bounds) {
      this.useFullGrid();
      return;
    }
    this.setWindow(this.paddedAndAligned(bounds));
  }

  includeMask(mask: ArrayLike<number>): void {
    const bounds = this.boundsForValues(mask, 0);
    if (!this.canUseAdaptiveWindow() || !bounds) return;
    const requested = this.paddedAndAligned(bounds);
    this.setWindow(unionBounds(this.currentBounds(), requested));
  }

  expandForDepth(depth: ArrayLike<number>): boolean {
    if (!this.canUseAdaptiveWindow() || this.isFullGrid()) return false;
    const wetBounds = this.boundsForValues(depth, this.options.wetDepthThresholdMeters);
    if (!wetBounds) return false;

    const margin = this.options.edgeMarginCells;
    const current = this.currentBounds();
    return this.expandEdges({
      west: wetBounds.minX <= current.minX + margin,
      north: wetBounds.minY <= current.minY + margin,
      east: wetBounds.maxX >= current.maxX - margin,
      south: wetBounds.maxY >= current.maxY - margin
    });
  }

  expandEdges(edges: WetDomainEdges): boolean {
    if (!this.canUseAdaptiveWindow() || this.isFullGrid()) return false;
    const tileSize = this.options.tileSizeCells;
    const current = this.currentBounds();
    const expanded: CellBounds = {
      minX: edges.west ? Math.max(0, current.minX - tileSize) : current.minX,
      minY: edges.north ? Math.max(0, current.minY - tileSize) : current.minY,
      maxX: edges.east ? Math.min(this.width - 1, current.maxX + tileSize) : current.maxX,
      maxY: edges.south ? Math.min(this.height - 1, current.maxY + tileSize) : current.maxY
    };
    if (sameBounds(current, expanded)) return false;
    this.setWindow(expanded);
    return true;
  }

  private canUseAdaptiveWindow(): boolean {
    return (
      this.options.enabled &&
      (this.width > this.options.tileSizeCells || this.height > this.options.tileSizeCells)
    );
  }

  private isFullGrid(): boolean {
    return (
      this.x === 0 &&
      this.y === 0 &&
      this.activeWidth === this.width &&
      this.activeHeight === this.height
    );
  }

  private useFullGrid(): void {
    this.x = 0;
    this.y = 0;
    this.activeWidth = this.width;
    this.activeHeight = this.height;
  }

  private currentBounds(): CellBounds {
    return {
      minX: this.x,
      minY: this.y,
      maxX: this.x + this.activeWidth - 1,
      maxY: this.y + this.activeHeight - 1
    };
  }

  private paddedAndAligned(bounds: CellBounds): CellBounds {
    const padding = this.options.initialPaddingCells;
    const tileSize = this.options.tileSizeCells;
    return {
      minX: Math.max(0, Math.floor((bounds.minX - padding) / tileSize) * tileSize),
      minY: Math.max(0, Math.floor((bounds.minY - padding) / tileSize) * tileSize),
      maxX: Math.min(
        this.width - 1,
        Math.ceil((bounds.maxX + padding + 1) / tileSize) * tileSize - 1
      ),
      maxY: Math.min(
        this.height - 1,
        Math.ceil((bounds.maxY + padding + 1) / tileSize) * tileSize - 1
      )
    };
  }

  private setWindow(bounds: CellBounds): void {
    this.x = bounds.minX;
    this.y = bounds.minY;
    this.activeWidth = bounds.maxX - bounds.minX + 1;
    this.activeHeight = bounds.maxY - bounds.minY + 1;
  }

  private boundsForValues(values: ArrayLike<number>, threshold: number): CellBounds | null {
    let minX = this.width;
    let minY = this.height;
    let maxX = -1;
    let maxY = -1;
    for (let index = 0; index < values.length; index += 1) {
      if ((values[index] ?? 0) <= threshold) continue;
      const x = index % this.width;
      const y = Math.floor(index / this.width);
      if (y >= this.height) break;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    return maxX < 0 ? null : { minX, minY, maxX, maxY };
  }
}

function unionBounds(left: CellBounds, right: CellBounds): CellBounds {
  return {
    minX: Math.min(left.minX, right.minX),
    minY: Math.min(left.minY, right.minY),
    maxX: Math.max(left.maxX, right.maxX),
    maxY: Math.max(left.maxY, right.maxY)
  };
}

function sameBounds(left: CellBounds, right: CellBounds): boolean {
  return (
    left.minX === right.minX &&
    left.minY === right.minY &&
    left.maxX === right.maxX &&
    left.maxY === right.maxY
  );
}
